/* @flow */
import LocaleUtil from "../uidispatcherapi/LocaleUtil";

import { BothIds } from "../types/BothIds";
import { ShoppingListItem } from "../types/ShoppingListItem";
import { PageType } from "../types/PageType";
import { ShoppingList, SortCriteria } from "../types/ShoppingList";
import { AvailabilityRenderData, ListInformation } from "../types/articlesearch/Article";
import { GdprStorage } from "../uidispatcherapi/GdprStorage";
import { SessionStore } from "../uidispatcherapi/SessionStore";
import { CIP_LISTID, DYNAMIC_LIST_IDS, RECENTLY_BOUGHT_LISTID } from "./ListOverviewUtils";

const REORDER = "reorder";
const HISTORY_LIST = "HISTORY";

const userListPageTypes = ["SAVED_CART", "HISTORY_PAGE", "REORDER", "SHOPPING_LIST"];

const STORAGE_KEY_LIST_SORT = "LISTPAGE_LAST_USED_SORTING";

export type SortCriteriaData = {
    CRITERIA: SortCriteria;
    I18N_KEY: string;
    TEST_CSS_CLASS: string;
    DESCRIPTION: string;
    VARIANT_BASED: boolean;
    SORT_PRIORITY: number;
};

export type ArticleSelectionListProps = {
    isSelected: boolean;
    showSelectionCheckbox: boolean;
    selectionAction: (bundleIds: string[], isSelect: boolean) => void;
};

const exported = {
    isAListPage(pageType: PageType): boolean {
        return userListPageTypes.includes(pageType);
    },

    filterItemsByIds<T extends { articleVariantId: string }>(items: T[], filterIds: string[]): T[] {
        const filterIdsSet = new Set(filterIds);
        return items.filter((item: T | undefined) => !!item && filterIdsSet.has(item.articleVariantId));
    },

    getSortCriteriaForSearch(sortCriteria: string | undefined | null) {
        if (sortCriteria === "asc" || sortCriteria === "desc") {
            return sortCriteria;
        }
        return "asc";
    },

    getBundles<T extends { articleVariantId: string; bettyBundleId: string; bundleId: string | undefined | null }>(
        items: T[],
        querySearchItemIds?: string[] | undefined | null
    ): string[] {
        const filteredItems = querySearchItemIds ? exported.filterItemsByIds(items, querySearchItemIds) : items;
        const results = (filteredItems ?? []).map((listItem) => {
            if (listItem.bettyBundleId) {
                return listItem.bettyBundleId;
            } else {
                // FIXME: we should make sure that all list items have a bettyBundleId - then this concatenation should
                // be removed
                return listItem.articleVariantId + listItem.bundleId;
            }
        });
        return results;
    },

    orderItemsByVariantId<T extends { bettyBundleId: string; articleVariantId: string }>(items: Array<T>): Array<T> {
        // Sort by bettyBundleId as main sorting, articleVariantId as secondary
        const sortedItemsBundleId = (items ?? []).sort((a, b) => a.bettyBundleId.localeCompare(b.bettyBundleId));
        const sortedItemsbyVariantId = sortedItemsBundleId.sort((a, b) => a.articleVariantId.localeCompare(b.articleVariantId));
        return sortedItemsbyVariantId;
    },

    getOrderedItems<T extends { bettyBundleId: string; articleVariantId: string }>(
        items: Array<T>,
        itemIdsInOrder: BothIds[]
    ): Array<T> {
        const mapped = (itemIdsInOrder ?? []).map((itemIds: BothIds) => {
            return (items ?? []).find((item) => {
                if (!item) {
                    return false;
                }
                // compare by bundle if possible (it's more precise):
                if (itemIds.bettyBundleId) {
                    return item.bettyBundleId === itemIds.bettyBundleId;
                }
                // otherwise compare by variant:
                return item.articleVariantId === itemIds.variantId;
            });
        });
        return mapped.filter((item): item is T => item !== null && item !== undefined);
    },

    // A dirty little hack to make flow happy, as the id field is set for carts, the listId for actual lists.
    // This is only necessary because in the RenameList and DeleteList it can be either a cart or a list.
    getId(list: { [x: string]: any }) {
        return list.id || list.listId;
    },

    displayCategoryView(sort: string) {
        return sort === exported.sortCategory;
    },

    hasPreviousUnmergedBundleId(item: { [x: string]: any }) {
        if (item.hasOwnProperty("followUpHistoryItems")) {
            return item.followUpHistoryItems.some((historyItem: any) => historyItem.merged === false);
        }
        return false;
    },

    getPreviousUnmergedBundleId(item: { [x: string]: any }) {
        if (exported.hasPreviousUnmergedBundleId(item)) {
            const historyElement = item.followUpHistoryItems.find((historyItem: any) => historyItem.merged === false);
            return (historyElement || {}).previousBettyBundleId;
        }
        return undefined;
    },

    getPreviousUnmergedBundle(item: { [x: string]: any }) {
        if (exported.hasPreviousUnmergedBundleId(item)) {
            return item.followUpHistoryItems.find((historyItem: any) => historyItem.merged === false);
        }
        return undefined;
    },

    getPreviousUnmergedBundleIdForVariant(
        itemList: {
            [x: string]: any;
        }[],
        variantId: string
    ): string | undefined | null {
        const itemFromList = itemList.find(item => item.articleVariantId === variantId);
        if (typeof itemFromList !== "undefined") {
            return exported.getPreviousUnmergedBundleId(itemFromList);
        }
        return undefined;
    },

    sortAndDuplicate(bothIds: BothIds[], itemsOrdered: string[]): BothIds[] {
        const withDuplicatesListOfLists: BothIds[][] = (itemsOrdered ?? []).map((variantId) => {
            const matchingItems: BothIds[] = (bothIds ?? []).filter((item) => {
                return item.variantId === variantId;
            });
            const itemsDuped: BothIds[] = matchingItems.map((matchingItem) => {
                return { variantId, bettyBundleId: matchingItem.bettyBundleId };
            });
            return itemsDuped;
        });
        const flattened: BothIds[] = withDuplicatesListOfLists.flat();
        return flattened;
    },

    sortWithoutDuplication(bothIds: BothIds[], itemsOrdered: string[]): BothIds[] {
        let bothIds2 = [...bothIds];
        const result: Array<BothIds | undefined | null> = (itemsOrdered ?? []).map((itemId) => {
            const found = bothIds2.find((item) => {
                if (!item) {
                    return false;
                }
                return item.variantId === itemId;
            });
            if (found) {
                bothIds2 = bothIds2.filter((x) => !(x.variantId === found.variantId && x.bettyBundleId === found.bettyBundleId));
            }
            return found;
        });
        return result.filter((item): item is BothIds => item !== null && item !== undefined);
    },

    adaptItemsOrderedForBundles(
        shoppingListItems: ShoppingListItem[],
        itemsOrdered: string[],
        needsDuplication: boolean
    ): BothIds[] {
        const bothIds: BothIds[] = (shoppingListItems ?? []).map((item) => {
            const variantId = item.articleVariantId;
            const bettyBundleId = item.bettyBundleId;
            return { variantId, bettyBundleId };
        });
        if (needsDuplication) {
            return exported.sortAndDuplicate(bothIds, itemsOrdered);
        }
        return exported.sortWithoutDuplication(bothIds, itemsOrdered);
    },

    sortCategory: "byCategory",
    sortAscending: "ascending",
    sortDescending: "descending",
    sortLexicographicalAZ: "lexicographicalAZ",
    sortLexicographicalZA: "lexicographicalZA",
    personalListOld: "PERSONAL",
    personalList: "personal list",
    storeList: "STORE",
    reorderList: REORDER,
    historyList: HISTORY_LIST,
    MIN_AMOUNT_TO_DISABLE_CATEGORY_SORT_ON_DYNAMIC_LIST: 1000,

    SORT_CRITERIA: {
        byCategory: {
            CRITERIA: "byCategory",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_CATEGORY",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-category",
            DESCRIPTION: "category",
            VARIANT_BASED: true,
            SORT_PRIORITY: 1
        },
        ascending: {
            CRITERIA: "ascending",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_ASC",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-ascending",
            DESCRIPTION: "date_added_asc",
            VARIANT_BASED: false,
            SORT_PRIORITY: 2
        },
        descending: {
            CRITERIA: "descending",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_DESC",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-descending",
            DESCRIPTION: "date_added_desc",
            VARIANT_BASED: false,
            SORT_PRIORITY: 3
        },
        asc: {
            CRITERIA: "asc",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_AZ",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-a-to-z",
            DESCRIPTION: "a_to_z",
            VARIANT_BASED: true,
            SORT_PRIORITY: 4
        },
        desc: {
            CRITERIA: "desc",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_ZA",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-by-z-to-a",
            DESCRIPTION: "z_to_a",
            VARIANT_BASED: true,
            SORT_PRIORITY: 5
        },
        recently_ordered: {
            CRITERIA: "recently_ordered",
            I18N_KEY: "EX_SHOPPINGLIST_SORT_RECENTLY_ORDERED_DESC",
            TEST_CSS_CLASS: "test-menu-item-list-page-sort-recently-ordered",
            DESCRIPTION: "recently_ordered",
            VARIANT_BASED: false,
            SORT_PRIORITY: 6
        }
    } as { [x: string]: SortCriteriaData },

    isVariantBased(sortCriteria: string): boolean | undefined | null {
        const found = Object.values(exported.SORT_CRITERIA).find((s) => {
            return s.CRITERIA === sortCriteria;
        });
        return found ? found.VARIANT_BASED : undefined;
    },

    shouldDisableBundleSelector(listType: string | undefined): true | undefined {
        return listType && listType === exported.storeList ? true : undefined;
    },

    readSortCriteria(): string | undefined | null {
        return GdprStorage.localStorage.get(STORAGE_KEY_LIST_SORT).actualResult;
    },

    writeSortCriteria(sort: string) {
        GdprStorage.localStorage.put(STORAGE_KEY_LIST_SORT, sort, GdprStorage.FUNCTIONAL);
    },

    doesCountryPreferDescendingSort(country: string) {
        return country === "CN";
    },

    getPossibleSortCriteriaForShoppingListPage(list: ShoppingList): { [x: string]: SortCriteriaData } {
        const allSortCriteria = exported.SORT_CRITERIA;
        const needsToRemoveCategorySort =
            exported.isDynamicList(list.listId) &&
            list.items.length > exported.MIN_AMOUNT_TO_DISABLE_CATEGORY_SORT_ON_DYNAMIC_LIST;
        const cleanedSortCriteria = needsToRemoveCategorySort
            ? Object.fromEntries(
                Object.entries(allSortCriteria).filter(([key]) => key !== "byCategory")
            )
            : allSortCriteria;

        return LocaleUtil.isSelectedLanguageSymbolic()
            ? Object.fromEntries(
                Object.entries(cleanedSortCriteria).filter(([key]) => key !== "asc" && key !== "desc")
            )
            : cleanedSortCriteria;
    },

    getSortCriteriaWithHighestPrio(possibleSortCriteria: { [x: string]: SortCriteriaData }) {
        return Object.values(possibleSortCriteria)
            .sort((a, b) => a.SORT_PRIORITY - b.SORT_PRIORITY)[0];
    },

    getSelectedSortCriteria(
        eventKey: string | undefined | null,
        list?: ShoppingList | undefined | null
    ): SortCriteriaData {
        const sortCritCleanedLanguage = LocaleUtil.isSelectedLanguageSymbolic()
            ? Object.fromEntries(
                Object.entries(exported.SORT_CRITERIA).filter(([key]) => key !== "asc" && key !== "desc")
            )
            : exported.SORT_CRITERIA;
        const possibleCrit: { [k: string]: SortCriteriaData } = list
            ? exported.getPossibleSortCriteriaForShoppingListPage(list)
            : sortCritCleanedLanguage;

        let selectedCriteria;
        const fromStorage = exported.readSortCriteria();
        if (fromStorage && possibleCrit[fromStorage]) {
            selectedCriteria = possibleCrit[fromStorage];
        }
        if (eventKey) {
            selectedCriteria = possibleCrit[eventKey];
        }
        if (!selectedCriteria) {
            if (exported.doesCountryPreferDescendingSort(SessionStore.getCountry())) {
                return possibleCrit.descending || exported.getSortCriteriaWithHighestPrio(possibleCrit);
            }
            return exported.getSortCriteriaWithHighestPrio(possibleCrit);
        }
        return selectedCriteria;
    },

    getEmptySearchQuery() {
        return {
            q: "",
            categoryPath: null,
            facets: [],
            ranges: [],
            options: []
        };
    },

    getUpdatedItemsList(storedList: ShoppingList, itemsToAdd: ShoppingListItem[], itemsToRemove: ShoppingListItem[]) {
        const storedBettyBundleIds = (storedList.items ?? []).map((item) => item.bettyBundleId);
        const itemsToAddNotAlreadyContained = itemsToAdd.filter(
            (item) => !storedBettyBundleIds.includes(item.bettyBundleId)
        );
        const itemsLeftFromList = (storedList.items ?? []).filter(
            (item) => !itemsToRemove.some((removeItem) => removeItem.bettyBundleId === item.bettyBundleId)
        );
        const updatedItems = [...itemsLeftFromList, ...itemsToAddNotAlreadyContained];
        return { ...storedList, items: updatedItems };
    },

    showReplacementLink(availabilityInfo: AvailabilityRenderData, listInformation: ListInformation): boolean {
        const showDueToAvailability =
            availabilityInfo && (availabilityInfo.availability === "UNAVAILABLE" || availabilityInfo.delisted);
        const isOnList = !!(listInformation && listInformation.listId);
        return isOnList && showDueToAvailability;
    },

    getStatusCodeOfError(error: any): string | null {
        try {
            if (error) {
                return JSON.parse(error.responseText).personalListStatusCode;
            }
        } catch (error) {
            return null;
        }
        return null;
    },

    getKeyAddItemsError(error: any) {
        return exported.getStatusCodeOfError(error) === "MAXIMUM_SIZE_LIMIT_EXCEEDED"
            ? "EX_ADD_TO_LIST_FAILED_SIZE_LIMIT_EXCEEDED"
            : "EX_ADD_TO_LIST_FAILED";
    },

    getKeyCreateListError(
        error:
            | {
                  [x: string]: any;
              }
            | undefined
            | null
    ) {
        return exported.getStatusCodeOfError(error) === "MAXIMUM_SIZE_LIMIT_EXCEEDED"
            ? "EX_CREATE_LIST_FAILED_SIZE_LIMIT_EXCEEDED"
            : "EX_CREATE_LIST_FAILED";
    },

    isDynamicList(listId: string) {
        return DYNAMIC_LIST_IDS.includes(listId);
    },

    isCIPList(listId: string) {
        return listId === CIP_LISTID;
    },

    getDynamicListKey(listId: string, isBrowserTitle: boolean | undefined) {
        if (listId === RECENTLY_BOUGHT_LISTID) {
            return isBrowserTitle
                ? "EX_LIST_RECENTLY_BOUGHT_ARTICLES_BROWSER_TITLE"
                : "EX_LIST_RECENTLY_BOUGHT_ARTICLES_HEADLINE";
        } else if (listId === CIP_LISTID) {
            return isBrowserTitle ? "EX_LIST_CIP_LIST_BROWSER_TITLE" : "EX_LIST_CIP_LIST_HEADLINE";
        }
        return null;
    },

    getBrowserTitleKey(list: ShoppingList | null | undefined) {
        const listId = list ? list.listId : null;
        const listName = list ? list.name : null;
        if (listId === null) {
            return {
                title: "EX_LOADING_ONGOING",
                additionalParam: null
            };
        }
        const dynamicListHeadlineKey = this.getDynamicListKey(listId, true);
        if (dynamicListHeadlineKey !== null) {
            return {
                title: dynamicListHeadlineKey,
                additionalParam: null
            };
        }
        return {
            title: "EX_PERSONAL_LIST_DOCUMENT_TITLE",
            additionalParam: { name: listName }
        };
    },

    isLockedForUser(list: ShoppingList) {
        return (list.readOnlyForEmployees && SessionStore.isEmployee()) || (list.readOnly && SessionStore.isCustomer());
    }
};

export default exported;
export { REORDER as reorderList, HISTORY_LIST as historyList };

export const {
    isAListPage,
    filterItemsByIds,
    getSortCriteriaForSearch,
    getBundles,
    orderItemsByVariantId,
    getOrderedItems,
    getId,
    displayCategoryView,
    hasPreviousUnmergedBundleId,
    getPreviousUnmergedBundleId,
    getPreviousUnmergedBundle,
    getPreviousUnmergedBundleIdForVariant,
    sortAndDuplicate,
    sortWithoutDuplication,
    adaptItemsOrderedForBundles,
    sortCategory,
    sortAscending,
    sortDescending,
    sortLexicographicalAZ,
    sortLexicographicalZA,
    personalListOld,
    personalList,
    storeList,
    MIN_AMOUNT_TO_DISABLE_CATEGORY_SORT_ON_DYNAMIC_LIST,
    SORT_CRITERIA,
    isVariantBased,
    shouldDisableBundleSelector,
    readSortCriteria,
    writeSortCriteria,
    doesCountryPreferDescendingSort,
    getPossibleSortCriteriaForShoppingListPage,
    getSortCriteriaWithHighestPrio,
    getSelectedSortCriteria,
    getEmptySearchQuery,
    getUpdatedItemsList,
    showReplacementLink,
    getStatusCodeOfError,
    getKeyAddItemsError,
    getKeyCreateListError,
    isDynamicList,
    isCIPList,
    getDynamicListKey,
    getBrowserTitleKey,
    isLockedForUser
} = exported;
